import React from 'react';
import { Box, Checkbox, Radio, Stack, TextField, Typography } from '@mui/material';
import { RequirementImage, RequirementOption, RequirementOptionGroup } from './calculatorApi';
import { CalcImageData, ImageThumb } from './CalcImage';

const toImageData = (images: RequirementImage[]): CalcImageData[] =>
    (images || []).map((i) => ({
        articleNumber: i.articleNumber,
        name: i.name,
        titel: i.titel,
        beschreibung: i.beschreibung,
        haupt: i.haupt,
        crop: i.crop,
    }));

type OptMap = Record<string, number>;
type OptUpdater = (fn: (prev: OptMap) => OptMap) => void;

const OptionRow: React.FC<{
    group: RequirementOptionGroup;
    option: RequirementOption;
    siblings: string[] | null;
    opt: OptMap;
    onChange: OptUpdater;
    depth: number;
}> = ({ group, option, siblings, opt, onChange, depth }) => {
    const menge = opt[option.requirementId] || 0;
    const selected = menge > 0;

    const setMenge = (val: number) =>
        onChange((prev) => {
            const next = { ...prev };
            if (val > 0) next[option.requirementId] = val;
            else delete next[option.requirementId];
            // Exklusivgruppe: das Aktivieren einer Option nullt die anderen derselben Gruppe.
            if (siblings && val > 0) {
                for (const s of siblings) if (s !== option.requirementId) delete next[s];
            }
            return next;
        });

    let control: React.ReactNode;
    if (group.inputKind === 'Spin') {
        control = (
            <TextField
                type="number"
                size="small"
                value={menge || ''}
                onChange={(e) => setMenge(Number(e.target.value) || 0)}
                slotProps={{ htmlInput: { min: 0, step: 1, style: { width: 64 } } }}
            />
        );
    } else if (siblings) {
        // Exklusive Option (Radio / exklusive Auswahl). Erneuter Klick hebt sie auf –
        // außer die Gruppe ist „mandatory" (dann bleibt immer eine gewählt).
        control = (
            <Radio
                size="small"
                checked={selected}
                onClick={() => {
                    if (selected) {
                        if (!group.mandatory) setMenge(0);
                    } else {
                        setMenge(option.defaultAnzahl || 1);
                    }
                }}
            />
        );
    } else {
        control = (
            <Checkbox
                size="small"
                checked={selected}
                onChange={(e) => setMenge(e.target.checked ? option.defaultAnzahl || 1 : 0)}
            />
        );
    }

    return (
        <Box sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {control}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{option.description || option.requirementId}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {option.requirementId}
                    </Typography>
                </Box>
                <ImageThumb title={option.description || option.requirementId} images={toImageData(option.images)} />
            </Stack>
            {option.optionGroups && option.optionGroups.length > 0 && selected && (
                <OptionsInput groups={option.optionGroups} opt={opt} onChange={onChange} depth={depth + 1} />
            )}
        </Box>
    );
};

/** Rekursive Options-Eingabe einer Instanz – gegliedert nach Optionsgruppen (mit Titel/Widget/Exklusivität). */
export const OptionsInput: React.FC<{
    groups: RequirementOptionGroup[];
    opt: OptMap;
    onChange: OptUpdater;
    depth?: number;
}> = ({ groups, opt, onChange, depth = 0 }) => (
    <Box
        sx={{
            mt: 0.5,
            pl: 1.5,
            ml: 1,
            borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
        }}
    >
        {groups.map((group, gi) => (
            <Box key={gi} sx={{ mt: gi > 0 ? 1 : 0.5 }}>
                {group.title && (
                    <Typography variant="caption" sx={{ fontWeight: 600 }} color="text.secondary">
                        {group.title}
                        {group.mandatory ? ' *' : ''}
                    </Typography>
                )}
                {group.options.map((option) => (
                    <OptionRow
                        key={option.requirementId}
                        group={group}
                        option={option}
                        siblings={group.exclusive ? group.options.map((x) => x.requirementId) : null}
                        opt={opt}
                        onChange={onChange}
                        depth={depth}
                    />
                ))}
            </Box>
        ))}
    </Box>
);
